/**
 * OnboardingTour - Welcome tutorial shown once on first use.
 * Designed for elderly users: full-screen on mobile, large framed screenshots of
 * each step, big text, big touch targets (>=48px), no swipe gestures.
 * Layout: thin surface bar (step counter) → phone frame whose screenshot scrolls
 * to its action button while a finger-tap plays over it → title + description on a
 * solid background → controls. Text never sits over the image (keeps contrast
 * high, which matters most for elderly users).
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bell, Gift, Sparkles, Ticket, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
}

interface OnboardingStep {
  key: string;
  icon: LucideIcon;
  /** Captura real en public/help/ (sin extensión). Si falta, el marco muestra el icono. */
  screenshot?: string;
  /** Posición vertical del centro del botón de acción dentro de la captura (0-1).
   *  La medimos con scripts/screenshots-manual/capture.mjs (imprime `frac=...`).
   *  Es estable: solo cambia si cambia el layout de esa pantalla. Con ella, el
   *  componente calcula a qué punto hacer scroll y dónde "pegar" el toque. */
  tapFrac?: number;
  /** Posición horizontal del toque (0-1). Por defecto 0.5 (centro), que es lo
   *  correcto para los botones a todo el ancho. Solo se ajusta cuando el objetivo
   *  no está centrado (p. ej. una tarjeta de la rejilla de servicios). */
  tapFracX?: number;
  /** Dimensiones naturales de la captura (px). Imprescindibles: reservan el aspect
   *  ratio ANTES de que cargue el PNG (sin esto, mientras carga la imagen no tiene
   *  altura → el clamp del scroll degenera y se ve negro arriba), y sirven para
   *  calcular cuánto scroll hay (→ duración por paso, para que el último no corra). */
  imgW?: number;
  imgH?: number;
}

// Cada paso anima la pantalla real de su acción: tocar "Tomar Turno", elegir
// servicio, acompañantes, confirmar. Pasos 3 y 4 reutilizan la captura del
// confirmar (#booking-section-4), tocando distinta zona.
// tapFrac medido con scripts/screenshots-manual/capture.mjs sobre las capturas
// actuales (paso-1 frac 0.151, paso-2 0.833, acompañantes 0.406, confirmar 0.956).
// imgW/imgH = dimensiones reales de los PNG en public/help/.
const STEPS: OnboardingStep[] = [
  { key: '1', icon: Sparkles, screenshot: 'paso-1-tomar-full', tapFrac: 0.151, imgW: 828, imgH: 4880 },
  { key: '2', icon: Ticket, screenshot: 'paso-2-servicio-full', tapFrac: 0.833, tapFracX: 0.74, imgW: 776, imgH: 1854 },
  { key: '3', icon: Users, screenshot: 'paso-5-confirmar-full', tapFrac: 0.406, imgW: 776, imgH: 3470 },
  { key: '4', icon: Bell, screenshot: 'paso-5-confirmar-full', tapFrac: 0.956, imgW: 776, imgH: 3470 },
  { key: '5', icon: Gift },
];

// Big phone-style frame shared by the screenshot and the icon fallback.
// 4/5 aspect (not full 9/16) so the image stays big but the title + description
// remain visible without scrolling — important for elderly users.
const FRAME_CLASS =
  'mx-auto w-[240px] max-w-[70vw] aspect-[4/5] rounded-[1.75rem] shadow-xl overflow-hidden';

// Animated phone frame visible viewport (px). The action button is scrolled to
// land near the vertical centre of this window before the tap plays.
const DEMO_FRAME_H = 300;
const DEMO_LAND_RATIO = 0.5;
// Guarantee a perceptible scroll on every step (when the screenshot is tall
// enough), so the motion clearly reads as "scroll to the button".
const DEMO_MIN_SCROLL = 120;

/**
 * AnimatedDemo — phone frame whose screenshot scrolls to its action button while a
 * realistic finger-tap plays over it.
 *
 * No JS measurement: the scroll distance and the tap position are PURE-CSS math
 * derived from `tapFrac` (the button's vertical fraction in the image). Because
 * `translateY(<percent>)` and `top: <percent>` resolve against the scrolling
 * layer's own height (= the image height), everything is correct on any device,
 * zoom, or cached image — there is nothing to measure that could fail and leave
 * the demo frozen with no scroll and no tap.
 *
 * --tour-scroll-end (the scroll-end transform) reads:
 *   clamp( FRAME_H - 100% ,  min( LAND - tapFrac% , -MIN_SCROLL ) ,  0 )
 *   · `min(LAND - tapFrac%, -MIN)`  scrolls the button to the landing line, but at
 *     least MIN_SCROLL so even top-of-screen buttons show clear motion.
 *   · `clamp(FRAME_H - 100%, …, 0)` keeps it within the image (no over-scroll past
 *     the bottom, no scrolling above the top).
 */
// Reference inner width of the animated frame (w-[230px] - 2*6px border). Only
// used to estimate the scroll distance for the per-step DURATION — the actual
// scroll/tap positions are width-independent CSS percentages.
const DEMO_REF_WIDTH = 218;

function AnimatedDemo({
  screenshot,
  tapFrac,
  tapFracX = 0.5,
  imgW = 1,
  imgH = 1,
  alt,
}: {
  screenshot: string;
  tapFrac: number;
  tapFracX?: number;
  imgW?: number;
  imgH?: number;
  alt: string;
}) {
  const land = DEMO_FRAME_H * DEMO_LAND_RATIO;
  const tapPct = tapFrac * 100;
  // Outer `min(0px, …)` guarantees the scroll is never POSITIVE (which would push
  // the image down and show the dark frame at the top) even in a transient state
  // where the layer's height isn't known yet. The aspect-ratio on the image makes
  // that state vanishingly brief, but this makes the black impossible regardless.
  const scrollEnd =
    `min(0px, clamp(${DEMO_FRAME_H}px - 100%, min(${land}px - ${tapPct}%, -${DEMO_MIN_SCROLL}px), 0px))`;

  // Estimate how far this step scrolls (at the reference width) and slow the loop
  // for long scrolls so the last step (which scrolls the whole confirm screen)
  // doesn't whip past. Short/medium scrolls keep the snappy 5s base.
  const displayedH = DEMO_REF_WIDTH * (imgH / imgW);
  const actionY = tapFrac * displayedH;
  const minScroll = Math.min(0, DEMO_FRAME_H - displayedH);
  let dist = Math.max(minScroll, Math.min(0, land - actionY));
  if (dist > -DEMO_MIN_SCROLL && minScroll <= -DEMO_MIN_SCROLL) dist = -DEMO_MIN_SCROLL;
  const scrollPx = Math.abs(dist);
  const duration = Math.min(8, 5 + Math.max(0, scrollPx - 250) * 0.007);

  return (
    <div className="mx-auto w-[230px] max-w-[70vw] rounded-[1.75rem] border-[6px] border-surface-3 bg-surface-3 shadow-xl">
      <div className="relative h-[300px] rounded-[1.4rem] overflow-hidden">
        {/* Scrolling layer: holds BOTH the screenshot and the glued tap, so the
            tap moves with the button instead of floating in frame space.
            --tour-duration drives the scroll AND tap animations (it inherits down
            to the tap pseudo-elements) so they stay in sync at any speed. */}
        <div
          className="tour-scroll-demo"
          style={
            {
              ['--tour-scroll-end']: scrollEnd,
              ['--tour-duration']: `${duration}s`,
            } as CSSProperties
          }
        >
          {/* width/height reserve the aspect ratio so the layer has its real height
              BEFORE the PNG loads — otherwise the scroll clamp degenerates while
              loading and shows the dark frame ("black at the top"). */}
          <img
            src={`/help/${screenshot}.png`}
            alt={alt}
            width={imgW}
            height={imgH}
            className="block w-full h-auto"
            style={{ aspectRatio: `${imgW} / ${imgH}` }}
          />
          <span
            className="tour-tap-demo"
            style={{ top: `${tapPct}%`, left: `${tapFracX * 100}%` }}
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

export function OnboardingTour({ isOpen, onClose }: OnboardingTourProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  // Always start from the first screen when the tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isLastStep = currentStep === STEPS.length - 1;
  const step = STEPS[currentStep];
  const StepIcon = step.icon;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-step-title"
        className="bg-surface border border-hairline w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-md sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scale-in"
      >
        {/* Thin bar: back button + step counter */}
        <div className="relative flex-shrink-0 bg-surface-2 border-b border-hairline px-4 py-3 flex items-center justify-center">
          {currentStep > 0 && (
            <button
              type="button"
              onClick={handleBack}
              aria-label={t('onboarding.back')}
              className="absolute top-1/2 -translate-y-1/2 left-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-ink-muted hover:text-ink hover:bg-surface-3 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" aria-hidden="true" />
            </button>
          )}
          <p className="text-ink text-base font-semibold tabular-nums">
            {t('onboarding.stepIndicator', {
              current: currentStep + 1,
              total: STEPS.length,
            })}
          </p>
        </div>

        {/* Scrollable content (big framed screenshot + title + description).
            Inner wrapper is min-h-full + justify-center so the block sits in the
            VERTICAL CENTRE of the free space (no ugly gap at the bottom), while
            still scrolling from the top if it ever grows taller than the window. */}
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col justify-center px-6 py-6 text-center">
          <div className="flex justify-center mb-5">
            {step.tapFrac != null && step.screenshot ? (
              // Animated phone frame: the screen scrolls to its action button and a
              // realistic finger-tap plays over it (loops). Self-calibrating from
              // tapFrac — see AnimatedDemo.
              <AnimatedDemo
                key={step.key}
                screenshot={step.screenshot}
                tapFrac={step.tapFrac}
                tapFracX={step.tapFracX}
                imgW={step.imgW}
                imgH={step.imgH}
                alt={t('onboarding.screenshotAlt', { number: currentStep + 1 })}
              />
            ) : step.screenshot ? (
              <div className={`${FRAME_CLASS} border-[6px] border-surface-3 bg-surface-3`}>
                <img
                  src={`/help/${step.screenshot}.png`}
                  alt={t('onboarding.screenshotAlt', { number: currentStep + 1 })}
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ) : (
              // No screenshot for this step (e.g. loyalty): show the icon big in the
              // frame, with a gentle pulse so it feels animated too.
              <div
                className={`${FRAME_CLASS} bg-surface-2 border border-hairline flex items-center justify-center`}
              >
                <StepIcon className="w-24 h-24 text-accent" aria-hidden="true" />
              </div>
            )}
          </div>

          <h2
            id="onboarding-step-title"
            className="font-sans text-2xl font-semibold tracking-tight text-ink mb-3"
          >
            {t(`onboarding.steps.${step.key}.title`)}
          </h2>
          <p className="text-lg text-ink-muted">
            {t(`onboarding.steps.${step.key}.description`)}
          </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex-shrink-0 px-6 pb-6 pt-2 border-t border-hairline">
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-5" aria-hidden="true">
            {STEPS.map((dot, index) => (
              <span
                key={dot.key}
                className={`rounded-full transition-all ${
                  index === currentStep
                    ? 'w-4 h-4 bg-accent'
                    : 'w-3 h-3 bg-surface-3'
                }`}
              />
            ))}
          </div>

          {/* Primary button: Next / Start */}
          <button
            type="button"
            onClick={handleNext}
            className="w-full min-h-[56px] py-4 px-6 bg-accent text-on-accent text-lg font-semibold rounded-xl hover:bg-accent-hover transition-colors focus:outline-none focus:ring-2 focus:ring-focus"
          >
            {isLastStep ? t('onboarding.start') : t('onboarding.next')}
          </button>

          {/* Secondary button: Skip (visible on every screen) */}
          <button
            type="button"
            onClick={onClose}
            className="w-full min-h-[48px] mt-3 py-3 px-6 text-lg font-medium text-ink-muted rounded-xl hover:bg-surface-2 transition-colors"
          >
            {t('onboarding.skip')}
          </button>
        </div>
      </div>
    </div>
  );
}
